import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ExternalLink, MapPin, Navigation, Maximize2, Minimize2, X } from 'lucide-react';
import { supabase } from '../../services/supabaseClient';
import { serviceRoleClient } from '../../services/serviceRoleClient';

const db = () => serviceRoleClient || supabase;

const parseCoord = (lat, lng) => {
  const a = Number(lat);
  const b = Number(lng);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
  if (a === 0 && b === 0) return null;
  if (a < -90 || a > 90 || b < -180 || b > 180) return null;
  return { lat: a, lng: b };
};

const haversineKm = (a, b) => {
  if (!a || !b) return null;
  const toRad = (n) => (n * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 6371 * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
};

const formatKm = (km) => {
  if (km == null || !Number.isFinite(km)) return '—';
  if (km < 1) return `${Math.round(km * 1000)} m`;
  return `${km.toFixed(km < 10 ? 1 : 0)} km`;
};

const extractLatLngFromText = (text) => {
  const s = String(text || '');
  const at = s.match(/@(-?\d+\.?\d*),\s*(-?\d+\.?\d*)/);
  if (at) return parseCoord(at[1], at[2]);
  const q = s.match(/[?&]q=(-?\d+\.?\d*),\s*(-?\d+\.?\d*)/);
  if (q) return parseCoord(q[1], q[2]);
  const ll = s.match(/(-?\d{1,2}\.\d+)\s*,\s*(-?\d{1,3}\.\d+)/);
  if (ll) return parseCoord(ll[1], ll[2]);
  return null;
};

const fmtTime = (value) => {
  if (!value) return 'N/A';
  return new Date(value).toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const cleanTrail = (points) => {
  if (!points.length) return [];
  const out = [points[0]];
  for (let i = 1; i < points.length; i += 1) {
    const prev = out[out.length - 1];
    const km = haversineKm(prev, points[i]);
    if (km == null) continue;
    const dt = Math.max(
      1,
      (new Date(points[i].created_at || 0).getTime() - new Date(prev.created_at || 0).getTime()) / 1000,
    );
    if (km < 0.015) continue;
    if (km > Math.max(3.5, (dt / 3600) * 160)) continue;
    out.push(points[i]);
  }
  return out;
};

const loadGoogleMaps = () => {
  if (typeof window === 'undefined') return Promise.reject(new Error('no window'));
  if (window.google?.maps?.Map) return Promise.resolve(window.google.maps);
  if (window.__rsGoogleMapsPromise) return window.__rsGoogleMapsPromise;

  window.__rsGoogleMapsPromise = (async () => {
    const { data, error } = await db().rpc('get_google_api_key');
    const key = typeof data === 'string' ? data.trim() : '';
    if (error || !key || key.includes('your_google') || key.includes('YOUR_ACTUAL')) {
      throw new Error('Google Maps key missing');
    }

    await new Promise((resolve, reject) => {
      const existing = document.getElementById('rs-google-maps-js');
      if (existing) {
        if (window.google?.maps?.Map) {
          resolve();
          return;
        }
        existing.addEventListener('load', () => resolve(), { once: true });
        existing.addEventListener('error', () => reject(new Error('Google Maps failed')), { once: true });
        return;
      }
      const script = document.createElement('script');
      script.id = 'rs-google-maps-js';
      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}&language=en&region=IN`;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Google Maps failed to load'));
      document.head.appendChild(script);
    });

    if (!window.google?.maps?.Map) throw new Error('Google Maps unavailable');
    return window.google.maps;
  })();

  return window.__rsGoogleMapsPromise;
};

const routeViaGoogle = (gmaps, points) => {
  const pts = (points || []).filter((p) => p && Number.isFinite(p.lat) && Number.isFinite(p.lng));
  if (pts.length < 2) return Promise.resolve(null);
  const service = new gmaps.DirectionsService();
  return new Promise((resolve) => {
    service.route(
      {
        origin: pts[0],
        destination: pts[pts.length - 1],
        waypoints: pts.slice(1, -1).slice(0, 23).map((p) => ({ location: p, stopover: false })),
        travelMode: gmaps.TravelMode.DRIVING,
        optimizeWaypoints: false,
      },
      (result, status) => {
        if (status !== 'OK' || !result?.routes?.[0]) {
          resolve(null);
          return;
        }
        const route = result.routes[0];
        const latlngs = route.overview_path.map((ll) => ({ lat: ll.lat(), lng: ll.lng() }));
        const km = (route.legs || []).reduce((sum, leg) => sum + (leg.distance?.value || 0), 0) / 1000;
        resolve({ latlngs, km });
      },
    );
  });
};

const fetchOsrmRoute = async (points) => {
  const pts = (points || []).filter((p) => p && Number.isFinite(p.lat) && Number.isFinite(p.lng));
  if (pts.length < 2) return null;
  const slim = pts.length > 80 ? pts.filter((_, i) => i === 0 || i === pts.length - 1 || i % Math.ceil(pts.length / 80) === 0) : pts;
  const path = slim.map((p) => `${p.lng},${p.lat}`).join(';');
  try {
    const res = await fetch(
      `https://router.project-osrm.org/route/v1/driving/${path}?overview=full&geometries=geojson&steps=false`,
    );
    if (!res.ok) return null;
    const json = await res.json();
    const route = json?.routes?.[0];
    const coords = route?.geometry?.coordinates;
    if (!Array.isArray(coords) || coords.length < 2) return null;
    return {
      latlngs: coords.map(([lng, lat]) => ({ lat, lng })),
      km: Number(route.distance || 0) / 1000,
    };
  } catch {
    return null;
  }
};

const snapTrailToRoads = async (gmaps, trail) => {
  if (!trail || trail.length < 2) return null;
  const parts = [];
  for (let i = 0; i < trail.length - 1; i += 24) {
    const chunk = trail.slice(i, Math.min(i + 25, trail.length));
    if (chunk.length < 2) break;
    const snapped = await routeViaGoogle(gmaps, chunk);
    if (snapped?.latlngs?.length) parts.push(snapped);
  }
  if (parts.length) {
    const latlngs = [];
    let km = 0;
    parts.forEach((part, idx) => {
      latlngs.push(...(idx === 0 ? part.latlngs : part.latlngs.slice(1)));
      km += part.km || 0;
    });
    return { latlngs, km };
  }
  return fetchOsrmRoute(trail);
};

const pinIcon = (gmaps, color) => ({
  path: 'M12 2C8.1 2 5 5.1 5 9c0 5.2 7 13 7 13s7-7.8 7-13c0-3.9-3.1-7-7-7zm0 9.5c-1.4 0-2.5-1.1-2.5-2.5S10.6 6.5 12 6.5s2.5 1.1 2.5 2.5S13.4 11.5 12 11.5z',
  fillColor: color,
  fillOpacity: 1,
  strokeColor: '#ffffff',
  strokeWeight: 1.4,
  scale: 1.5,
  anchor: new gmaps.Point(12, 22),
  labelOrigin: new gmaps.Point(12, 9),
});

export default function ComplaintMechanicMap({
  complaint,
  mechanicRow,
  bargainPartners = [],
  trackMechanicId: trackMechanicIdProp,
  onTrackMechanicIdChange,
  embedded = false,
  compact = false,
}) {
  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const overlaysRef = useRef({ markers: [], lines: [], fitKey: '' });
  const [mapError, setMapError] = useState('');
  const [trackLocal, setTrackLocal] = useState('');
  const [fullscreen, setFullscreen] = useState(false);
  const [googleKm, setGoogleKm] = useState({ traveled: null, remaining: null });

  const status = String(complaint?.request_status || complaint?.status || '').toLowerCase();
  const isCancelled = status.includes('cancel');
  const isClosed = isCancelled || status.includes('complete') || status.includes('closed');
  const assignedId = complaint?.assigned_mechanic_id ? String(complaint.assigned_mechanic_id) : '';
  const trackMechanicId = String(trackMechanicIdProp || trackLocal || assignedId || bargainPartners[0]?.id || '');
  const setTrackMechanicId = (id) => {
    setTrackLocal(id);
    onTrackMechanicIdChange?.(id);
  };
  const trackingAssigned = !!assignedId && trackMechanicId === assignedId;
  const trackedBargain = bargainPartners.find((p) => String(p.id) === trackMechanicId) || null;
  const isLive = !isClosed && (!!assignedId || bargainPartners.length > 0);

  const customerPin = useMemo(() => {
    return (
      parseCoord(complaint?.pickup_latitude, complaint?.pickup_longitude) ||
      parseCoord(complaint?.latitude, complaint?.longitude) ||
      extractLatLngFromText(complaint?.location_link) ||
      extractLatLngFromText(complaint?.pickup_address) ||
      extractLatLngFromText(complaint?.address)
    );
  }, [complaint]);

  const shopPin = useMemo(() => {
    if (trackingAssigned) {
      return parseCoord(mechanicRow?.service_center_lat, mechanicRow?.service_center_lng);
    }
    return parseCoord(trackedBargain?.service_center_lat, trackedBargain?.service_center_lng);
  }, [mechanicRow, trackingAssigned, trackedBargain]);

  const livePin = useMemo(() => {
    if (trackingAssigned) {
      return (
        parseCoord(complaint?.partner_live_lat, complaint?.partner_live_lng) ||
        shopPin ||
        parseCoord(complaint?.mechanic_latitude, complaint?.mechanic_longitude)
      );
    }
    return parseCoord(trackedBargain?.live_lat, trackedBargain?.live_lng) || shopPin;
  }, [complaint, shopPin, trackingAssigned, trackedBargain]);

  const { data: pings = [] } = useQuery({
    queryKey: ['complaintLocationTrail', complaint?.id],
    queryFn: async () => {
      const { data, error } = await db()
        .from('rsa_job_location_pings')
        .select('id, latitude, longitude, created_at, mechanic_id')
        .eq('service_request_id', complaint.id)
        .order('created_at', { ascending: true })
        .limit(500);
      if (error) return [];
      return (data || [])
        .map((p) => ({
          ...p,
          mechanic_id: p.mechanic_id ? String(p.mechanic_id) : '',
          ...parseCoord(p.latitude, p.longitude),
        }))
        .filter((p) => p.lat != null);
    },
    enabled: !!complaint?.id,
    refetchInterval: isLive ? 5000 : false,
  });

  const trail = useMemo(() => {
    const raw = Array.isArray(pings) ? pings : [];
    let mine = trackMechanicId
      ? raw.filter((p) => !p.mechanic_id || p.mechanic_id === String(trackMechanicId))
      : raw;
    if (mine.length < 2 && raw.length > mine.length) mine = raw;
    return cleanTrail(mine);
  }, [pings, trackMechanicId]);

  const hasActualPath = trail.length >= 2;
  const lastPing = trail.length ? trail[trail.length - 1] : null;
  const mechanicPin = lastPing || livePin;
  const startPin = trail[0] || shopPin || mechanicPin;
  const traveledFallback = trail.slice(1).reduce((sum, p, i) => sum + (haversineKm(trail[i], p) || 0), 0);
  const remainingFallback = haversineKm(mechanicPin, customerPin);
  const traveledKm = googleKm.traveled ?? traveledFallback;
  const remainingKm = googleKm.remaining ?? remainingFallback;
  const fromStartKm = haversineKm(startPin, customerPin);

  useEffect(() => {
    let cancelled = false;
    if (!mapEl.current) return undefined;
    if (!customerPin && !mechanicPin) return undefined;

    loadGoogleMaps()
      .then(async (gmaps) => {
        if (cancelled || !mapEl.current) return;
        let map = mapRef.current;
        if (!map) {
          map = new gmaps.Map(mapEl.current, {
            center: customerPin || mechanicPin,
            zoom: 13,
            mapTypeId: 'roadmap',
            fullscreenControl: false,
            streetViewControl: false,
            mapTypeControl: false,
            scaleControl: true,
            zoomControl: true,
            clickableIcons: false,
            gestureHandling: 'greedy',
          });
          mapRef.current = map;
        }

        const overlays = overlaysRef.current;
        overlays.markers.forEach((m) => m.setMap(null));
        overlays.lines.forEach((l) => l.setMap(null));
        overlays.markers = [];
        overlays.lines = [];

        const addMarker = (pos, title, color, label) => {
          const marker = new gmaps.Marker({
            map,
            position: pos,
            title,
            icon: pinIcon(gmaps, color),
            label: label
              ? { text: label, color: '#fff', fontSize: '10px', fontWeight: '700' }
              : undefined,
            zIndex: title === 'Mechanic' ? 900 : 600,
          });
          overlays.markers.push(marker);
          return marker;
        };

        const addLine = (path, color, dashed) => {
          if (!path?.length) return;
          const line = new gmaps.Polyline({
            map,
            path,
            strokeColor: color,
            strokeOpacity: dashed ? 0 : 0.92,
            strokeWeight: dashed ? 4 : 5,
            icons: dashed
              ? [
                  {
                    icon: { path: 'M 0,-1 0,1', strokeOpacity: 1, strokeColor: color, scale: 3 },
                    offset: '0',
                    repeat: '14px',
                  },
                ]
              : undefined,
          });
          overlays.lines.push(line);
        };

        const [driven, remain] = await Promise.all([
          hasActualPath ? snapTrailToRoads(gmaps, trail) : Promise.resolve(null),
          !isClosed && mechanicPin && customerPin
            ? routeViaGoogle(gmaps, [mechanicPin, customerPin])
            : Promise.resolve(null),
        ]);
        if (cancelled) return;

        const drivenPath = driven?.latlngs?.length
          ? driven.latlngs
          : trail.length >= 2
            ? trail.map((p) => ({ lat: p.lat, lng: p.lng }))
            : [];
        addLine(drivenPath, isCancelled ? '#ea580c' : '#1d4ed8', false);
        trail.forEach((p) => {
          const dot = new gmaps.Circle({
            map,
            center: { lat: p.lat, lng: p.lng },
            radius: 22,
            fillColor: '#1d4ed8',
            fillOpacity: 0.28,
            strokeColor: '#1d4ed8',
            strokeOpacity: 0.7,
            strokeWeight: 1,
            clickable: false,
          });
          overlays.lines.push(dot);
        });

        const remainKm = remain?.km;
        const showRemain = remain?.latlngs?.length >= 2 && (remainKm == null || remainKm > 0.08);
        if (showRemain) addLine(remain.latlngs, '#0f766e', true);

        if (customerPin) addMarker(customerPin, 'Customer', '#dc2626', 'C');
        const startFar = startPin && mechanicPin && (haversineKm(startPin, mechanicPin) || 0) > 0.08;
        if (startFar) addMarker(startPin, 'Start', '#475569', 'S');
        if (mechanicPin) addMarker(mechanicPin, 'Mechanic', isCancelled ? '#dc2626' : '#0f766e', 'M');

        setGoogleKm({
          traveled: driven?.km ?? traveledFallback ?? null,
          remaining: remain?.km ?? null,
        });

        const bounds = new gmaps.LatLngBounds();
        [customerPin, mechanicPin, startFar ? startPin : null, ...drivenPath, ...(showRemain ? remain.latlngs : [])]
          .filter(Boolean)
          .forEach((p) => bounds.extend(p));
        const fitKey = [
          customerPin?.lat,
          customerPin?.lng,
          mechanicPin?.lat,
          mechanicPin?.lng,
          drivenPath.length,
          showRemain ? remain.latlngs.length : 0,
        ].join(':');
        gmaps.event.trigger(map, 'resize');
        if (!bounds.isEmpty() && overlays.fitKey !== fitKey) {
          map.fitBounds(bounds, 56);
          overlays.fitKey = fitKey;
        }
      })
      .catch((e) => {
        if (!cancelled) setMapError(e.message || 'Google Maps failed to load');
      });

    return () => {
      cancelled = true;
    };
  }, [
    customerPin?.lat,
    customerPin?.lng,
    mechanicPin?.lat,
    mechanicPin?.lng,
    startPin?.lat,
    startPin?.lng,
    trail,
    isCancelled,
    isClosed,
    hasActualPath,
  ]);

  useEffect(() => {
    if (!fullscreen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setFullscreen(false);
    };
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [fullscreen]);

  useEffect(() => {
    const t = setTimeout(() => {
      const gmaps = window.google?.maps;
      const map = mapRef.current;
      if (!gmaps || !map) return;
      gmaps.event.trigger(map, 'resize');
    }, 80);
    return () => clearTimeout(t);
  }, [fullscreen]);

  useEffect(
    () => () => {
      overlaysRef.current.markers.forEach((m) => m.setMap(null));
      overlaysRef.current.lines.forEach((l) => l.setMap(null));
      overlaysRef.current = { markers: [], lines: [], fitKey: '' };
      mapRef.current = null;
    },
    [],
  );

  if (!customerPin && !mechanicPin) {
    return (
      <div className={embedded ? 'py-4 text-center' : 'bg-white rounded-xl shadow-sm border border-gray-200 p-6'}>
        {!embedded ? (
          <div className="flex items-center gap-2 mb-2">
            <MapPin size={20} className="text-teal-600" />
            <h2 className="text-xl font-bold text-gray-900">Mechanic map</h2>
          </div>
        ) : null}
        <p className="text-sm text-gray-500">
          Is complaint par GPS pin nahi hai (customer location / mechanic live). Naye assigned jobs par partner
          app live location bhejti hai.
        </p>
      </div>
    );
  }

  const assignedName =
    mechanicRow?.display_name || mechanicRow?.shop_name || mechanicRow?.mechanic_name || '';
  const lastUpdate =
    (trackingAssigned && complaint?.partner_live_updated_at) ||
    trackedBargain?.live_updated_at ||
    lastPing?.created_at;
  const showRemainStat = remainingKm != null && remainingKm > 0.08;
  const showTrackSelect = !isClosed && (assignedId || bargainPartners.length > 0);
  const openMapsHref =
    mechanicPin && customerPin
      ? `https://www.google.com/maps/dir/${mechanicPin.lat},${mechanicPin.lng}/${customerPin.lat},${customerPin.lng}`
      : customerPin
        ? `https://www.google.com/maps?q=${customerPin.lat},${customerPin.lng}`
        : `https://www.google.com/maps?q=${mechanicPin.lat},${mechanicPin.lng}`;

  const shellClass = fullscreen
    ? 'fixed inset-0 z-[80] bg-white flex flex-col'
    : embedded
      ? 'flex flex-col'
      : 'bg-white rounded-xl shadow-sm border border-gray-200 p-6 flex flex-col';

  return (
    <div className={shellClass}>
      <div className={`flex items-center justify-between gap-2 ${fullscreen ? 'px-4 py-3 border-b border-slate-200' : 'mb-2'}`}>
        <div className="flex items-center gap-2 min-w-0">
          {fullscreen || !embedded ? <Navigation size={18} className="text-teal-600 shrink-0" /> : null}
          {fullscreen || !embedded ? (
            <p className="text-sm font-semibold text-slate-900 truncate">Mechanic map</p>
          ) : null}
          {isLive ? (
            <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold uppercase text-emerald-800">
              Live
            </span>
          ) : isCancelled ? (
            <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-bold uppercase text-red-700">
              Cancelled
            </span>
          ) : (
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase text-slate-600">
              Done
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <a
            href={openMapsHref}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
          >
            <ExternalLink size={13} />
            Google Maps
          </a>
          <button
            type="button"
            onClick={() => setFullscreen((v) => !v)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
          >
            {fullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
            {fullscreen ? 'Exit' : 'Full screen'}
          </button>
        </div>
      </div>

      {showTrackSelect ? (
        <div className={fullscreen ? 'px-4 pb-2' : 'mb-2'}>
          <select
            value={trackMechanicId}
            onChange={(e) => setTrackMechanicId(e.target.value)}
            className="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm text-slate-800"
          >
            {assignedId ? (
              <option value={assignedId}>Assigned{assignedName ? ` · ${assignedName}` : ''}</option>
            ) : (
              <option value="">
                {bargainPartners.length ? 'Select quoting mechanic' : 'No mechanic quote yet'}
              </option>
            )}
            {bargainPartners
              .filter((p) => String(p.id) !== assignedId)
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.status === 'pending' ? 'Quoted' : p.status} ₹{Number(p.amount || 0).toLocaleString('en-IN')}
                  {p.name ? ` · ${p.name}` : ''}
                </option>
              ))}
          </select>
        </div>
      ) : assignedName ? (
        <p className={`text-xs text-slate-500 ${fullscreen ? 'px-4 pb-2' : 'mb-2'}`}>
          Assigned · {assignedName}
        </p>
      ) : null}

      {mapError ? (
        <div className={`relative ${fullscreen ? 'flex-1 min-h-0' : ''}`}>
          <iframe
            title="Google Maps"
            src={
              mechanicPin && customerPin
                ? `https://maps.google.com/maps?output=embed&saddr=${mechanicPin.lat},${mechanicPin.lng}&daddr=${customerPin.lat},${customerPin.lng}`
                : `https://maps.google.com/maps?output=embed&q=${(customerPin || mechanicPin).lat},${(customerPin || mechanicPin).lng}&z=14`
            }
            className={`${
              fullscreen ? 'h-full min-h-[320px]' : compact ? 'h-72' : embedded ? 'h-[420px]' : 'h-80'
            } w-full rounded-lg border border-slate-200`}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      ) : (
        <div className={`relative ${fullscreen ? 'flex-1 min-h-0' : ''}`}>
          <div
            ref={mapEl}
            className={`${
              fullscreen
                ? 'h-full min-h-[320px] rounded-none border-0'
                : compact
                  ? 'h-72 rounded-lg border border-slate-200'
                  : embedded
                    ? 'h-[420px] rounded-lg border border-slate-200'
                    : 'h-80 rounded-lg border border-slate-200'
            } w-full overflow-hidden z-0`}
          />

          <div className="pointer-events-none absolute left-2 top-2 flex flex-wrap gap-1.5">
            <span className="rounded-md bg-white/95 px-2 py-1 text-[11px] font-semibold text-slate-800 shadow-sm border border-slate-200">
              {hasActualPath
                ? `Travelled ${formatKm(traveledKm)} · ${trail.length} pings`
                : `Actual path nahi · ${trail.length || 0} GPS ping`}
            </span>
            <span className="rounded-md bg-white/95 px-2 py-1 text-[11px] font-semibold text-slate-800 shadow-sm border border-slate-200">
              {showRemainStat ? `Left ${formatKm(remainingKm)}` : 'Reached'}
            </span>
            {fromStartKm != null ? (
              <span className="rounded-md bg-white/95 px-2 py-1 text-[11px] font-semibold text-slate-800 shadow-sm border border-slate-200">
                From start {formatKm(fromStartKm)}
              </span>
            ) : null}
          </div>

          <div className="pointer-events-none absolute left-2 bottom-8 rounded-lg bg-white/95 px-2.5 py-2 text-[11px] text-slate-700 shadow-sm border border-slate-200 max-w-[220px]">
            {hasActualPath ? (
              <p className="flex items-center gap-1.5">
                <span className="inline-block h-0.5 w-5 rounded bg-blue-700" />
                Actual GPS path
              </p>
            ) : (
              <p className="text-amber-800 font-semibold">
                Partner app ne is job ka rasta record nahi kiya. Shop–customer line guess hoti, isliye nahi dikhai.
              </p>
            )}
            {showRemainStat && !isClosed ? (
              <p className="mt-1 flex items-center gap-1.5">
                <span className="inline-block h-0.5 w-5 rounded border-t-2 border-dashed border-teal-700" />
                Suggested to customer
              </p>
            ) : null}
            <p className="mt-1 flex items-center gap-1.5">
              <span className="inline-block h-2 w-2 rounded-full bg-red-600" />
              Customer
            </p>
            <p className="mt-1 flex items-center gap-1.5">
              <span className="inline-block h-2 w-2 rounded-full bg-teal-700" />
              Mechanic
            </p>
            {lastUpdate ? (
              <p className="mt-1.5 text-[10px] text-slate-400">Updated {fmtTime(lastUpdate)}</p>
            ) : null}
          </div>

          {fullscreen ? (
            <button
              type="button"
              onClick={() => setFullscreen(false)}
              className="absolute right-2 top-2 inline-flex items-center justify-center rounded-lg bg-white/95 p-2 text-slate-700 shadow-sm border border-slate-200 hover:bg-white"
              aria-label="Close full screen"
            >
              <X size={16} />
            </button>
          ) : null}
        </div>
      )}
    </div>
  );
}
